import { isTraverseSelector, findElement, type TraverseSelector } from '@source-digital/traverse-selector'
import { type EmbedElementSettings } from '../embedder'
import { toNumber } from './to-number'

type MaybeStringOrHTMLElement = string | HTMLElement | undefined
type MaybeStringOrHTMLElementArray = string | HTMLElement | HTMLElement[] | undefined

export function resolveElement (el: MaybeStringOrHTMLElement, rootEl: HTMLElement | Document = document): HTMLElement | undefined {
  if (el == null) return undefined
  if (typeof el === 'string') {
    return rootEl.querySelector(el) as HTMLElement ?? undefined
  }
  if (el instanceof HTMLElement) {
    return el
  }
  return undefined
}

export function resolveElements (el: MaybeStringOrHTMLElementArray, rootEl: HTMLElement | Document = document): HTMLElement[] {
  if (el == null) return []
  if (typeof el === 'string') {
    return Array.from(rootEl.querySelectorAll(el))
  }
  if (el instanceof HTMLElement) {
    return [el]
  } else if (Array.isArray(el)) {
    return el
  }
  return []
}

/**
 * Resolve element with traverse selector
 * @param selector
 * @param targetEl
 * @returns
 */
export function resolveElementWithTraverse (selector: string | HTMLElement | TraverseSelector, targetEl: HTMLElement): HTMLElement | undefined {
  if (isTraverseSelector(selector)) {
    const _selector = {
      ...selector,
      traverse: typeof selector.traverse === 'string' ? selector.traverse.split(',').map(s => s.trim()) : selector.traverse
    }
    return findElement(targetEl, _selector) as HTMLElement ?? undefined
  } else {
    return resolveElement(selector)
  }
}

/**
 *
 *
 * @param target - the selector that we are trying to find
 * @param root - the root element to observe (we expect the target to be a descendent of this element)
 * @param timeout - optional: how long to wait before timing out
 * @return
 */
export async function resolveElementsByObserver (
  target: MaybeStringOrHTMLElementArray,
  root: MaybeStringOrHTMLElement,
  timeout?: number
): Promise<HTMLElement[]> {
  const rootEl = resolveElement(root ?? document.body)
  if (rootEl == null) return []

  timeout = toNumber(timeout, 0)

  let resolve: (value: HTMLElement[] | PromiseLike<HTMLElement[]>) => void
  let reject: (reason?: any) => void
  let timeoutId: number | undefined
  let observer: MutationObserver

  // create a promise that will resolve when the target selector is found
  const promise = new Promise<HTMLElement[]>((_resolve, _reject) => {
    resolve = _resolve
    reject = _reject
  })

  // setup a timeout if specified
  const setupTimeout = () => {
    // if a timeout is provided, reject the promise after the timeout
    if (timeout) {
      timeoutId = setTimeout(() => {
        cleanup()
        reject(new Error(`Timeout after ${timeout}ms`))
      }, timeout)
    }
  }

  // this is the mutation observer callback, it will be called when the DOM changes
  // it just detects if the right set of mutations have been observed and then
  // triggers a check for matches of the target selector
  const handleMutatations = (mutations: MutationRecord[]) => {
    const relevantMutation = mutations.some(mutation => mutation.type === 'childList')
    if (relevantMutation) {
      checkForMatches()
    }
  }

  // this checks for matches of the target selector
  const checkForMatches = () => {
    const matches = resolveElements(target, rootEl)
    if (matches.length > 0) {
      cleanup()
      resolve(matches)
      return true
    }
  }

  // this cleans up the observer and timeout
  const cleanup = () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId)
    }
    observer?.disconnect()
  }

  // setup the observer
  const setupObserver = () => {
    observer = new MutationObserver(handleMutatations)

    // Start observing the root node for configured mutations
    observer.observe(rootEl, {
      childList: true,
      subtree: true
    })
  }

  if (!checkForMatches()) {
    setupObserver()
    setupTimeout()
  }

  return await promise
}

/**
 *
 *
 * @param target - the selector that we are trying to find
 * @param root - the root element to observe (we expect the target to be a descendent of this element)
 * @param interval - optional: how often to check for the target selector: default 1000ms
 * @param timeout - optional: how long to wait before timing out: default 10000ms
 * @return
 */
export async function resolveElementsByInterval (
  target: MaybeStringOrHTMLElementArray,
  root: MaybeStringOrHTMLElement,
  interval: number = 1000,
  timeout?: number
): Promise<HTMLElement[]> {
  const rootEl = resolveElement(root ?? document.body)
  if (rootEl == null) return []

  timeout = toNumber(timeout, 0)

  let tries = 0
  let resolve: (value: HTMLElement[] | PromiseLike<HTMLElement[]>) => void
  let reject: (reason?: any) => void
  let intervalId: number | undefined

  // create a promise that will resolve when the target selector is found
  const promise = new Promise<HTMLElement[]>((_resolve, _reject) => {
    resolve = _resolve
    reject = _reject
  })

  // this checks for matches of the target selector
  const checkForMatches = () => {
    const matches = resolveElements(target, rootEl)
    if (matches.length > 0) {
      cleanup()
      resolve(matches)

      return true
    }
  }

  /// this sets up the interval, it will reject the promise if the timeout is reached
  const setupInterval = () => {
    intervalId = setInterval(() => {
      tries += 1

      if (timeout && tries > timeout / interval) {
        cleanup()
        reject(new Error(`Timeout after ${timeout}ms`))
        return
      }

      checkForMatches()
    }, interval)
  }

  const cleanup = () => {
    if (intervalId != null) {
      clearInterval(intervalId)
    }
  }

  if (!checkForMatches()) {
    setupInterval()
  }

  return await promise
}

/**
 *
 *
 * @param el - the selector that we are trying to find
 * @param watchOptions - options for watching elements to be embedded
 * @return
 */
export async function watchForElements (el: MaybeStringOrHTMLElementArray, watchOptions?: EmbedElementSettings['watch']): Promise<HTMLElement[]> {
  // first try to resolve the element
  const matches = resolveElements(el)
  if (matches.length > 0) return matches

  const {
    mode, options
  } = watchOptions ?? {}

  const {
    rootEl, timeout, interval
  } = options ?? {}

  switch (mode) {
    case 'observe':
      return await resolveElementsByObserver(el, rootEl, timeout)
    case 'interval':
      return await resolveElementsByInterval(el, rootEl, interval, timeout)
    default:
      return resolveElements(el)
  }
}
